import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';

export interface AnimationState {
  scale: number;
  angle: number;
  lineDashOffset: number;
}

@Injectable({
  providedIn: 'root',
})
export class DetectionAnimationService implements OnDestroy {
  private animationFrameId: number | null = null;
  private isAnimating: boolean = false;
  private subscriberCount: number = 0;
  private animationState$ = new Subject<AnimationState>();

  // Animation state
  private scale: number = 1;
  private angle: number = 0;
  private lineDashOffset: number = 0;

  // Animation constants
  private readonly ANGLE_INCREMENT = Math.PI / 150;
  private readonly LINE_DASH_OFFSET_INCREMENT = 0.25;
  private readonly SCALE_MIN = 0.9;
  private readonly SCALE_MAX = 1.2;

  constructor() {
    // Service is singleton, will be created once
  }

  /**
   * Subscribe to animation updates
   * Returns an Observable that emits animation state on each frame
   */
  getAnimationState(): Observable<AnimationState> {
    return this.animationState$.asObservable();
  }

  /**
   * Start the animation loop when first subscriber registers
   */
  startAnimation(): void {
    this.subscriberCount++;
    if (!this.isAnimating && this.subscriberCount > 0) {
      this.isAnimating = true;
      this.animate();
    }
  }

  /**
   * Stop the animation loop when last subscriber unregisters
   */
  stopAnimation(): void {
    if (this.subscriberCount > 0) {
      this.subscriberCount--;
    }
    if (this.subscriberCount <= 0) {
      this.subscriberCount = 0;
      this.isAnimating = false;
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    }
  }

  /**
   * Get current animation state synchronously
   */
  getCurrentState(): AnimationState {
    return {
      scale: this.scale,
      angle: this.angle,
      lineDashOffset: this.lineDashOffset,
    };
  }

  /**
   * Main animation loop
   */
  private animate(): void {
    if (!this.isAnimating) {
      return;
    }

    // Update animation state
    this.updateScale();
    this.updateLineDashOffset();

    // Emit state to all subscribers
    this.animationState$.next({
      scale: this.scale,
      angle: this.angle,
      lineDashOffset: this.lineDashOffset,
    });

    // Schedule next frame
    this.animationFrameId = requestAnimationFrame((): void => this.animate());
  }

  /**
   * Update scale and angle for pulsing effect
   */
  private updateScale(): void {
    this.angle += this.ANGLE_INCREMENT;
    this.scale = this.SCALE_MIN + Math.abs(Math.sin(this.angle)) * (this.SCALE_MAX - this.SCALE_MIN);
  }

  /**
   * Update line dash offset for moving dashed line effect
   */
  private updateLineDashOffset(): void {
    this.lineDashOffset += this.LINE_DASH_OFFSET_INCREMENT;
    // Reset to prevent overflow (optional, but good practice)
    if (this.lineDashOffset > 1000) {
      this.lineDashOffset = 0;
    }
  }

  /**
   * Reset animation state (useful for cleanup or restart)
   */
  reset(): void {
    this.scale = 1;
    this.angle = 0;
    this.lineDashOffset = 0;
  }

  ngOnDestroy(): void {
    this.stopAnimation();
    this.animationState$.complete();
  }
}

